import { castDraft, Immutable, produce } from 'immer';

import { Router } from '@tdp';
import { IAzureAdAppWriter } from '@tdp/common/azureAdApp';
import { createCommands } from '@tdp/common/commandHandler';
import { lroSlice, LongRunningOperation } from '@tdp/common/lro';
import { notificationSlice, NotificationType } from '@tdp/common/notification';
import { nextCallStack } from '@tdp/common/shared/wellKnownPromises';
import { ILogger } from '@tdp/common/telemetry';

import { BotFrameworkBot } from './models';
import { IBotProvider, IBotWriter } from './services';
import { botSlice } from './slice';

export interface BotCreationLro {
  botCreated: boolean;
  appCreated: boolean;
}

export interface CreateBotCommandHandlerDependencies {
  botProvider: IBotProvider;
  botWriter: IBotWriter;
  azureAdAppWriter: IAzureAdAppWriter;
  router: Router;
  logger: ILogger;
}
export const createBotCommandHandler = ({
  botProvider,
  botWriter,
  azureAdAppWriter,
  router,
  logger
}: CreateBotCommandHandlerDependencies) =>
  createCommands({
    name: 'botCommands',
    handlers: {
      async *loadBotDetails({ id }: { id: string }) {
        yield botSlice.actions.botDetailsLoading();
        const botDetailsResult = await botProvider.readBotDetails(id);
        if (botDetailsResult.type === 'error') {
          yield botSlice.actions.botDetailsLoadingFailed();
          yield notificationSlice.actions.showNotification({
            message: `Failed to load bot: ${id} | ${botDetailsResult.errorType}`,
            show: true,
            autoDismiss: true,
            type: NotificationType.Error,
          });
          return;
        }
        yield botSlice.actions.loadBotDetails(botDetailsResult.botDetails);
        yield botSlice.actions.botDetailsLoadingSuccess();
      },
      async *loadBots() {
        yield botSlice.actions.botsLoading();
        const botResponse = await botProvider.getBots();
        const bots = botResponse.type !== 'success' ? [] : botResponse.bots;
        yield botSlice.actions.botsLoaded(bots);
      },
      async *createBot({
        name,
        lro,
        navigateOnSuccess = false,
      }: {
        name: string;
        lro: LongRunningOperation<BotCreationLro>;
        navigateOnSuccess?: boolean;
      }) {
        yield lroSlice.actions.lroCreated(lro);
        const azureAdApp = await azureAdAppWriter.createAzureAdApp(name);
        yield lroSlice.actions.lroUpdated(
          produce(lro, (nextLro) => {
            nextLro.state.appCreated = true;
          })
        );
        const bot = await botWriter.createBot({
          name,
          botId: azureAdApp.appId,
          callingEndpoint: '',
          messagingEndpoint: '',
        });
        yield lroSlice.actions.lroUpdated(
          produce(lro, (nextLro) => {
            nextLro.state.appCreated = true;
            nextLro.state.botCreated = true;
          })
        );
        yield botSlice.actions.newBotCreated(bot);
        yield lroSlice.actions.lroCompleted(
          produce(lro, (nextLro) => {
            nextLro.state.appCreated = true;
            nextLro.state.botCreated = true;
          })
        );
        logger.logUserBi({ telemetryMarker: 'api-action-new-bot-create-succcess', message: `New bot created successfully` });

        if (navigateOnSuccess) {
          await nextCallStack();
          // navigate to the bot's configuration page
          await router.navigate(`/tools/bots/${bot.botId}`);
        }
      },
      async *updateBot(bot: Immutable<BotFrameworkBot>) {
        // TODO(nibeauli): we should read back the value to ensure outside writes get reflected in the UI.
        await botWriter.putBot(bot);
        yield botSlice.actions.botUpdated(castDraft(bot));
        logger.logUserBi({ telemetryMarker: 'api-action-bot-update-success', message: `Bot with id ${bot.botId} updated successfully` });
      },
    },
  });
